<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('leave.employee_shift_permissions')">
      <oxd-form @submit-valid="filterItems" @reset="onReset">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :label="$t('general.employee')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="filters.fromDate"
                :label="$t('general.from_date')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="filters.toDate"
                :label="$t('general.to_date')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.status"
                type="select"
                :label="$t('general.status')"
                :options="statusOptions"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            type="reset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <br />

    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('leave.employee_shift_permissions') }}
        </oxd-text>
      </div>
      <oxd-form-row class="orangehrm-shift-permission-tabs">
        <oxd-button
          v-for="tab in typeTabs"
          :key="String(tab.value)"
          :display-type="activeType === tab.value ? 'secondary' : 'ghost'"
          :label="tab.label"
          @click="onTypeTabChange(tab.value)"
        />
      </oxd-form-row>
      <div class="orangehrm-container">
        <oxd-card-table
          :headers="headers"
          :items="items?.data"
          :loading="isLoading"
          :selectable="false"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
  </div>
  <shift-permission-comments-modal
    v-if="showCommentModal"
    :id="commentModalState"
    @close="onCommentModalClose"
  />
  <delete-confirmation
    ref="cancelDialog"
    :message="$t('leave.confirm_cancel_shift_permission')"
  />
</template>

<script>
import {computed, ref} from 'vue';
import {APIService} from '@ohrm/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import usei18n from '@/core/util/composable/usei18n';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';
import useLeaveActions from '@/orangehrmLeavePlugin/util/composable/useLeaveActions';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import ShiftPermissionCommentCountCell from '@/orangehrmLeavePlugin/components/ShiftPermissionCommentCountCell';
import ShiftPermissionCommentsModal from '@/orangehrmLeavePlugin/components/ShiftPermissionCommentsModal';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {
  formatLocalDateTime12h,
  parseServerDateTime,
  resolveExpectedTimeDisplay,
  TYPE_LATE_ARRIVAL,
} from '@/orangehrmLeavePlugin/util/shiftPermissionExpectedTime';

const resolveFilterStatusId = (status) => {
  if (status === null || status === undefined) {
    return undefined;
  }
  if (typeof status === 'object') {
    const id = status.id;
    if (id === null || id === undefined) {
      return undefined;
    }
    return id;
  }
  if (typeof status === 'number') {
    return status;
  }
  if (typeof status === 'string' && status.trim() !== '') {
    const parsed = Number(status);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const defaultFilters = {
  employee: null,
  fromDate: null,
  toDate: null,
  status: null,
};

export default {
  name: 'EmployeeShiftPermissionsList',

  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'shift-permission-comments-modal': ShiftPermissionCommentsModal,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();
    const filters = ref({...defaultFilters});
    const activeType = ref(null);
    const statusOptions = computed(() => [
      {id: null, label: $t('admin.all')},
      {id: 1, label: $t('leave.pending_approval')},
      {id: 2, label: $t('time.approved')},
      {id: 3, label: $t('leave.rejected')},
      {id: 0, label: $t('leave.cancelled')},
    ]);
    const query = computed(() => {
      const params = {
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
      };
      if (filters.value.employee?.id) {
        params.empNumber = filters.value.employee.id;
      }
      const statusId = resolveFilterStatusId(filters.value.status);
      if (statusId !== undefined) {
        params.status = statusId;
      }
      if (activeType.value) {
        params.type = activeType.value;
      }
      return params;
    });
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/employees/shift-permission-requests',
    );
    const lateHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/late-permission-requests',
    );
    const earlyHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/early-leave-requests',
    );
    const {leaveActions} = useLeaveActions(http);
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      isLoading,
      response,
      execQuery,
    } = usePaginate(http, {
      query,
      prefetch: false,
      normalizer: (data) => {
        return data.map((item) => {
          const submittedAt = parseServerDateTime(item.submittedAt);
          const employee = item.employee || {};

          return {
            ...item,
            empNumber: employee.empNumber,
            employeeDisplay: $tEmpName(employee, {excludePastEmpTag: false}),
            dateDisplay: item.date,
            submittedAtDisplay: submittedAt
              ? formatLocalDateTime12h(submittedAt)
              : item.submittedAt,
            expectedTimeDisplay: resolveExpectedTimeDisplay(item),
            statusDisplay: item.statusName,
            commentCount: item.commentCount ?? 0,
          };
        });
      },
    });

    return {
      filters,
      activeType,
      statusOptions,
      http,
      lateHttp,
      earlyHttp,
      leaveActions,
      showPaginator,
      currentPage,
      total,
      pages,
      isLoading,
      items: response,
      execQuery,
    };
  },

  data() {
    return {
      typeTabs: [
        {value: null, label: this.$t('admin.all')},
        {value: 'late_arrival', label: this.$t('leave.late_arrival')},
        {value: 'early_leave', label: this.$t('leave.early_leave')},
      ],
      showCommentModal: false,
      commentModalState: null,
      cancelRow: null,
    };
  },

  computed: {
    expectedTimeColumnTitle() {
      if (this.activeType === 'early_leave') {
        return this.$t('leave.expected_departure_time');
      }
      if (this.activeType === 'late_arrival') {
        return this.$t('leave.expected_arrival_time');
      }
      return this.$t('leave.expected_arrival_or_departure');
    },
    headers() {
      return [
        {
          name: 'employeeDisplay',
          title: this.$t('general.employee'),
          style: {flex: 1},
        },
        {
          name: 'dateDisplay',
          title: this.$t('general.date'),
          style: {flex: 1},
        },
        {
          name: 'typeName',
          title: this.$t('general.type'),
          style: {flex: 1},
        },
        {
          name: 'expectedTimeDisplay',
          title: this.expectedTimeColumnTitle,
          style: {flex: 1},
        },
        {
          name: 'statusDisplay',
          title: this.$t('general.status'),
          style: {flex: 1},
        },
        {
          name: 'commentCount',
          title: this.$t('general.comments'),
          style: {flex: '0 0 9rem'},
          cellRenderer: this.commentCountCellRenderer,
        },
        {
          name: 'actions',
          title: this.$t('general.actions'),
          slot: 'action',
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.cellRenderer,
          style: {flex: 1},
        },
      ];
    },
  },

  async beforeMount() {
    await this.applyQueryParams();
    await this.filterItems();
  },

  methods: {
    async applyQueryParams() {
      const params = new URLSearchParams(window.location.search);
      const empNumber = params.get('empNumber');
      const fromDate = params.get('fromDate');
      const toDate = params.get('toDate');
      const status = params.get('status');
      const type = params.get('type');

      if (fromDate) {
        this.filters.fromDate = fromDate;
      }
      if (toDate) {
        this.filters.toDate = toDate;
      }
      if (status !== null && status !== '') {
        const statusId = Number(status);
        if (!Number.isNaN(statusId)) {
          const match = this.statusOptions.find((opt) => opt.id === statusId);
          this.filters.status = match ?? statusId;
        }
      }
      if (type) {
        this.activeType = type;
      }
      if (empNumber) {
        try {
          const response = await this.http.request({
            method: 'GET',
            url: `/api/v2/pim/employees/${empNumber}`,
          });
          const employee = response.data?.data;
          if (employee) {
            this.filters.employee = {
              id: employee.empNumber,
              label: formatEmployeeFullName(employee),
              isPastEmployee: Boolean(employee.terminationId),
            };
          }
        } catch {
          // Employee lookup failed; search still runs with other URL filters.
        }
      }
    },
    commentCountCellRenderer(index, count, header, row) {
      return {
        component: ShiftPermissionCommentCountCell,
        props: {
          count: count ?? 0,
          onClick: () => this.openCommentsModal(row),
        },
      };
    },
    cellRenderer(...[, , , row]) {
      const cellConfig = {};
      const {approve, reject, more} = this.leaveActions;
      const dropdownActions = [
        {
          label: this.$t('general.add_comment'),
          context: 'add_comment',
        },
        {
          label: this.$t('leave.view_shift_permission_details'),
          context: 'view_details',
        },
        {
          label: this.$t('leave.view_pim_info'),
          context: 'pim_details',
        },
      ];

      if (row.canApprove) {
        approve.props.label = this.$t('general.approve');
        approve.props.onClick = () => this.onReview(row, 'APPROVE');
        cellConfig.approve = approve;
      }
      if (row.canReject) {
        reject.props.label = this.$t('general.reject');
        reject.props.onClick = () => this.onReview(row, 'REJECT');
        cellConfig.reject = reject;
      }
      if (row.canCancel) {
        dropdownActions.push({
          label: this.$t('leave.cancel_shift_permission'),
          context: 'cancel_shift_permission',
        });
      }

      more.props.options = dropdownActions;
      more.props.onClick = ($event) => this.onDropdownAction($event, row);
      cellConfig.more = more;

      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    openCommentsModal(row) {
      this.commentModalState = row.id;
      this.showCommentModal = true;
    },
    onDropdownAction(event, row) {
      switch (event.context) {
        case 'add_comment':
          this.openCommentsModal(row);
          break;
        case 'view_details':
          navigate('/leave/viewShiftPermission/{id}', {id: row.id});
          break;
        case 'pim_details':
          navigate('/pim/viewPersonalDetails/empNumber/{id}', {
            id: row.empNumber,
          });
          break;
        case 'cancel_shift_permission':
          this.confirmCancel(row);
          break;
        default:
          break;
      }
    },
    async confirmCancel(row) {
      this.cancelRow = row;
      const confirmation = await this.$refs.cancelDialog.showDialog();
      if (confirmation === 'ok') {
        this.onCancel(row);
      }
      this.cancelRow = null;
    },
    reviewHttpForRow(row) {
      return row.type === TYPE_LATE_ARRIVAL ? this.lateHttp : this.earlyHttp;
    },
    onTypeTabChange(type) {
      this.activeType = type;
      this.filterItems();
    },
    async filterItems() {
      await this.execQuery();
    },
    async onReset() {
      this.filters = {...defaultFilters};
      this.activeType = null;
      navigate('/leave/viewEmployeeShiftPermissions');
      await this.execQuery();
    },
    onReview(row, action) {
      this.isLoading = true;
      this.reviewHttpForRow(row)
        .update(row.id, {action})
        .then(() => {
          this.$toast.updateSuccess();
          return this.execQuery();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onCancel(row) {
      this.isLoading = true;
      this.reviewHttpForRow(row)
        .update(row.id, {action: 'CANCEL'})
        .then(() => {
          this.$toast.updateSuccess();
          return this.execQuery();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onCommentModalClose() {
      this.commentModalState = null;
      this.showCommentModal = false;
      this.execQuery();
    },
  },
};
</script>

<style scoped lang="scss">
.orangehrm-shift-permission-tabs {
  padding: 0.5rem 1rem;
  gap: 0.5rem;
  display: flex;
}

::v-deep(.card-footer-slot) {
  .oxd-table-cell-actions {
    justify-content: flex-end;
  }
  .oxd-table-cell-actions > * {
    margin: 0 !important;
  }
}
</style>
